import React, { useState, useEffect } from 'react';
import { localApi, ActiveTest } from '../utils/local-api';
import { api, UserExamRestrictions } from '../utils/api';
import { useIsMobile } from '../hooks/useIsMobile';
import DashboardDesktop from './DashboardDesktop';
import DashboardMobile from './DashboardMobile';
import { useAuthStore } from '../store';

const Dashboard: React.FC = () => {
  const isMobile = useIsMobile();
  const token = useAuthStore(state => state.token);
  const [activeTests, setActiveTests] = useState<ActiveTest[]>([]);
  const [historyCount, setHistoryCount] = useState<Record<string, number>>({});
  const [restrictions, setRestrictions] = useState<UserExamRestrictions | null>(null);

  useEffect(() => {
    if (!token) return;

    api.getUserRestrictions(token)
      .catch(() => ({
        success: true,
        hsk2Levels: [],
        hsk3Levels: [],
        cscaLevels: [],
        hsk2Restricted: true,
        hsk3Restricted: true,
        cscaRestricted: true,
      } as UserExamRestrictions))
      .then(setRestrictions);

    Promise.all([
      localApi.getActiveTests(token).catch(() => ({ active: [] })),
      localApi.getHistory(token).catch(() => []),
    ]).then(([activeRes, historyRes]) => {
      if (activeRes && activeRes.active) {
        setActiveTests(activeRes.active);
      }
      const counts: Record<string, number> = {};
      const history = Array.isArray(historyRes) ? historyRes : (historyRes as any).tests || [];
      history.forEach((h: any) => {
        const key = `${h.bank_version || '2.0'}-${h.system_type || 'simulation'}-${h.level}-${h.part}-${h.workbook_part || ''}-${h.paper_id || ''}-${h.topic_coarse || ''}-${h.topic_fine || ''}`;
        counts[key] = (counts[key] || 0) + 1;
      });
      setHistoryCount(counts);
    });
  }, [token]);

  if (!restrictions) {
    return <div className="py-20 text-center font-semibold text-[#312E81]">正在读取考试权限…</div>;
  }

  const visibleActiveTests = activeTests.filter(test => {
    if (test.bank_version === 'CSCA') {
      return !restrictions.cscaRestricted && restrictions.cscaLevels.includes(test.level);
    }
    if (test.bank_version === '3.0') {
      return !restrictions.hsk3Restricted && restrictions.hsk3Levels.includes(test.level);
    }
    return !restrictions.hsk2Restricted && restrictions.hsk2Levels.includes(test.level);
  });

  return isMobile ? (
    <DashboardMobile activeTests={visibleActiveTests} historyCount={historyCount} restrictions={restrictions} />
  ) : (
    <DashboardDesktop activeTests={visibleActiveTests} historyCount={historyCount} restrictions={restrictions} />
  );
};

export default Dashboard;
